import { startOfTomorrow } from 'date-fns/startOfTomorrow';
import React, { useCallback } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import { useNextBankingDayDispatch } from '../../../fetchHooks/useBankingDays';
import type { RecurringPaymentAgreement } from '../../../generated-types';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import { EikaModal } from '../../EikaModal';
import UpdateRecurringTransfer, { ANALYTICS_CATEGORY } from './UpdateRecurringTransfer';
import { RecurringTransferActionType } from './form/actions';
import getInitialRecurringTransferUpdateState from './form/getInitialRecurringTransferUpdateState';
import recurringTransferReducer from './form/reducer';
import useFormContext, { RecurringTransferFormContext } from './form/useFormContext';
import useValidate from './form/validate';

interface Props {
    recurringTransfer: RecurringPaymentAgreement;
    show: boolean;
    onClose: () => void;
    refresh: () => void;
}

const tomorrow = getZonedDate(startOfTomorrow());

const RecurringTransferDetails: React.FC<Props> = ({ recurringTransfer, show, onClose, refresh }) => {
    const form = useFormContext();

    const { handleChange, state } = form;
    const { signingOperationPending, signingStatus, signingError, dispatch } = useSigningContext();

    useNextBankingDayDispatch(tomorrow, handleChange, RecurringTransferActionType.ONLY_BANKING_DATE);

    const updateOnChange = useCallback(() => {
        const hasChanged = !!signingError || !!signingStatus || signingOperationPending;
        if (hasChanged && state.fromAccount) {
            refresh();
        }
    }, [signingError, signingStatus, signingOperationPending, state.fromAccount, refresh]);

    const onCloseUpdate = useCallback(() => {
        dispatch({ type: Actions.RESET });
        updateOnChange();
        onClose();
        const redirectUrl = new URLSearchParams(window.location.href).get('redirecturl');
        if (redirectUrl) window.location.replace(redirectUrl);
    }, [onClose, updateOnChange, dispatch]);

    return (
        <EikaModal
            id="recurringTransferDetails"
            className="recurring-transfer-details__modal"
            open={show}
            setOpen={() => null}
            onClose={onCloseUpdate}
            timeOut={signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            size="wide"
            desktopMinHeight="small"
            appNamespace="dagligbank-lokalbank-web"
            dataTrackName={paymentTrackIDs.recurring.transferName}
        >
            <UpdateRecurringTransfer
                recurringTransfer={recurringTransfer}
                paymentOperationPending={signingOperationPending}
                paymentStatus={signingStatus}
                onClose={onClose}
            />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORY}>
        <RecurringTransferFormContext
            validateHook={useValidate}
            reducer={recurringTransferReducer}
            initialState={getInitialRecurringTransferUpdateState(props.recurringTransfer)}
        >
            <RecurringTransferDetails {...props} />
        </RecurringTransferFormContext>
    </SigningContextProvider>
);
